<template>
  <ComplianceReviewModalShell
    :show="show"
    :title="modalTitle"
    :subtitle="currentHireId ? progressText : undefined"
    :loading="loading"
    :applicable-compliances="applicableCompliances"
    :empty-message="$t('No applicable compliances found for this worker.')"
    :main-title="mainTitle"
    :rail-title="railTitle"
    cancel-data-test="compliance-review-cancel"
    @close="$emit('close')"
  >
    <template v-if="review" #summary>
      <ComplianceReviewSummary
        :status="review.status"
        :reviewer="review.reviewedBy"
        :reviewed-at="formatDate(review.reviewedAt)"
        :next-review-at="review.status === VerifiedWorkerComplianceStatus.Cleared ? formatDate(review.nextReviewAt) : null"
        :note="review.status === VerifiedWorkerComplianceStatus.ChangesRequested ? review.workerMessage : null"
        :completed-count="review.completedCount"
        :applicable-count="review.applicableCount"
        :hide-header="!!currentHireId"
      />
    </template>
    <template v-if="!currentHireId" #rail>
      <ComplianceHiresPane :trusted-contact-id="trustedContactId" @count="hireCount = $event" />
    </template>
    <template #footer>
      <ComplianceReviewDecision
        v-if="complianceReviewEnabled && workerId && user.hasPermission('manage-compliance-tracker')"
        :worker-id="workerId"
        :worker-name="result?.trustedContact?.worker?.name ?? undefined"
        :incomplete-count="incompleteCount"
        :loading="loading"
        @success="handleSaved"
      />
      <div v-else class="tw-flex tw-justify-end">
        <WdsButton tertiary data-test="compliance-review-cancel" @click="$emit('close')">
          {{ $t('Close') }}
        </WdsButton>
      </div>
    </template>
  </ComplianceReviewModalShell>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsButton } from '@worksome/wds-components'
import { graphql } from '@/gql'
import { ComplianceName, VerifiedWorkerComplianceStatus } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import ComplianceReviewModalShell from '@/components/compliances/ComplianceReviewModalShell.vue'
import ComplianceHiresPane from '@/components/compliances/ComplianceReviewModal/ComplianceHiresPane.vue'
import ComplianceReviewSummary from '@/components/compliances/ComplianceReviewModal/ComplianceReviewSummary.vue'
import ComplianceReviewDecision from '@/components/compliances/ComplianceReviewModal/ComplianceReviewDecision.vue'
import { useAccountSettings } from '@/composables/useAccountSettings'
import useDate from '@/composables/useDate'
import { user } from '@/composables/useUser'

const props = defineProps<{
  show: boolean
  trustedContactId: string
  // Set when the review opens from a hire page, where the worker's other hires are left out.
  currentHireId?: string | null
}>()

const emit = defineEmits<{ close: []; saved: [] }>()

function handleSaved() {
  emit('saved')
  emit('close')
}

const ComplianceReviewModalQuery = graphql(`
  query ComplianceReviewModal($id: ID!) {
    trustedContact(id: $id) {
      id
      worker {
        id
        name
      }
      workerReview {
        status
        completedCount
        applicableCount
        reviewedAt
        nextReviewAt
        workerMessage
        reviewedBy {
          id
          name
          avatar
        }
      }
      compliances {
        name
        applicable
        ...ComplianceSection
      }
    }
  }
`)

const { result, loading } = useQuery(
  ComplianceReviewModalQuery,
  () => ({ id: props.trustedContactId }),
  { clientId: 'publicApi' },
)

const hireCount = ref<number | null>(null)

const railTitle = computed(() =>
  hireCount.value === null ? $t('Hires') : `${$t('Hires')} ${hireCount.value}`,
)

const workerId = computed(() => result.value?.trustedContact?.worker?.id ?? '')

const modalTitle = computed(() => {
  const name = result.value?.trustedContact?.worker?.name
  return name ? $t('Review :name', { name }) : $t('Review')
})

// The review summary takes the place of the worker review's own section.
const applicableCompliances = computed(() =>
  (result.value?.trustedContact?.compliances ?? []).filter(
    (c) => c.applicable && c.name !== ComplianceName.VerifiedWorkerCompliance,
  ),
)

const review = computed(() => result.value?.trustedContact?.workerReview ?? null)

const formatDate = (date?: string | null) => (date ? useDate(date).date.value : null)

const progressText = computed(() =>
  review.value
    ? $t(':done of :total complete', {
        done: review.value.completedCount,
        total: review.value.applicableCount,
      })
    : undefined,
)

const mainTitle = computed(() =>
  review.value
    ? `${$t('Compliance')} ${review.value.completedCount}/${review.value.applicableCount}`
    : $t('Compliance'),
)

const incompleteCount = computed(() =>
  review.value ? review.value.applicableCount - review.value.completedCount : 0,
)

const complianceReviewEnabled = useAccountSettings('compliance_tracker_enabled')
</script>
